import { useMemo } from 'react'

import { useProducts } from './useProducts'

import type { AccessSettingKey } from 'constants/accessSettings'

/**
 * How many products a single request will look at when counting overrides.
 *
 * A CAP, not a page size — see the honesty note below. 200 is chosen to cover the
 * realistic brand catalog (the largest seeded company here holds 26; the biggest
 * figure quoted anywhere in the conventions is "a brand with 400 SKUs") while
 * bounding the worst case to one request rather than a paging loop on a settings
 * page.
 */
const PRODUCT_SCAN_CAP = 200

export interface AccessOverrideCount {
  /**
   * Products whose own `collect_*` column is non-null, i.e. that pin the flag
   * rather than inheriting the company default.
   */
  products: number
  /**
   * True when the catalog is larger than `PRODUCT_SCAN_CAP`, so `products` is a
   * floor and not the answer. Callers MUST render this differently — "at least N"
   * — because a capped count presented as exact is worse than no count.
   */
  productsPartial: boolean
}

export interface UseAccessOverrideCountsResult {
  counts: Record<AccessSettingKey, AccessOverrideCount>
  isLoading: boolean
  /**
   * Certificates are NOT counted. Always `false` today; the field exists so the
   * rollup asks the hook rather than hard-coding the gap into its own copy, and so
   * flipping it on is a one-line change here when the endpoint lands.
   */
  certificatesCounted: boolean
}

const EMPTY: AccessOverrideCount = { products: 0, productsPartial: false }

/**
 * VRG-591 — how many records override each company-level "Certificate access"
 * flag, for the rollup beside the Settings -> Features toggles.
 *
 * ## The gap this closes, and the half of it that stays open
 *
 * Once products and certificates can override the two flags, the Features page's
 * toggles stop being the whole answer — and until now NOTHING anywhere reported
 * which records disagreed with them. A brand could not answer "what do we actually
 * collect", and could not have worked it out even in principle: there is no filter
 * for it on either list surface. The count is the smallest thing that restores the
 * question's answerability.
 *
 * PRODUCTS are counted. CERTIFICATES are not, and the rollup says so rather than
 * implying zero — which would reproduce the original defect in a more confident
 * voice.
 *
 * ## Why this derives client-side, and why that is a STOPGAP
 *
 * Counting belongs in one `SELECT count(*) … WHERE collect_email IS NOT NULL`
 * per scope, behind one endpoint returning four integers. That endpoint does not
 * exist, and `apps/api` is another agent's surface, so this is built against what
 * does: `GET /products/admin/company` already returns `collect_email` and
 * `collect_location` per row.
 *
 * The cost is real and should not be hidden: this pulls up to 200 full product
 * DTOs — description HTML, image arrays and all — to produce two integers. It is
 * acceptable because the request is shared with the rest of the app through
 * react-query's `['products','list',…]` key and a 5-minute `staleTime`, and
 * because a settings page is not a hot path. It is NOT acceptable as a permanent
 * shape.
 *
 * CERTIFICATES CANNOT BE DONE THE SAME WAY, and the difference is not squeamishness:
 * `CertificatesListAdminRequestQueryDto` has no override filter, and a company's
 * certificate count is unbounded in a way its catalog is not (49 seeded here; tens
 * of thousands is the design point). A capped scan would return a number that is
 * confidently wrong, which is strictly worse than an admitted absence.
 *
 * ## What the replacement should be
 *
 * `GET /certificate-trigger-settings/:companyId/override-counts` ->
 * `{ collect_email: { products, certificates }, collect_location: { … } }`,
 * gated by the same permission that reads the settings. Two indexed partial counts
 * per flag; no pagination, no payload. Swap the body of this hook for it and both
 * `productsPartial` and `certificatesCounted` stop being needed.
 */
export const useAccessOverrideCounts = (
  options?: { enabled?: boolean },
): UseAccessOverrideCountsResult => {
  const enabled = options?.enabled !== false

  const { data, isLoading } = useProducts({ limit: PRODUCT_SCAN_CAP }, { enabled })

  const counts = useMemo<Record<AccessSettingKey, AccessOverrideCount>>(() => {
    const rows = data?.data ?? []
    // `total` is the catalog size; `rows.length` is what we actually looked at.
    const partial = (data?.total ?? 0) > rows.length

    const countFor = (key: AccessSettingKey): AccessOverrideCount => ({
      // `!= null` deliberately, not a truthy check: `false` is a real override
      // ("this product does NOT require an email") and is exactly the case a
      // brand most needs surfaced. Only `null`/`undefined` mean "inherits".
      products: rows.filter(row => row[key] != null).length,
      productsPartial: partial,
    })

    return {
      collect_email: countFor('collect_email'),
      collect_location: countFor('collect_location'),
    }
  }, [data])

  return {
    counts: enabled ? counts : { collect_email: EMPTY, collect_location: EMPTY },
    isLoading: enabled && isLoading,
    certificatesCounted: false,
  }
}
